import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Card, CardContent, Chip } from '@mui/material';
import {
  Search as SearchIcon,
  Groups,
} from '@mui/icons-material';
import MdcvVerifiedBadge from '../components/icons/MdcvVerifiedBadge';
import MdiRolodexIcon from '../components/icons/MdiRolodexIcon';
import SearchBar from '../components/SearchBar';

const HomePage = () => {
  const navigate = useNavigate();
  // Search handled by shared SearchBar

  const features = [
    {
      icon: <MdiRolodexIcon fontSize="large" color="primary" />,
      title: 'Comprehensive Directory',
      description: 'Access profiles of over 9 million healthcare professionals nationwide',
    },
    {
      icon: <MdcvVerifiedBadge fontSize="large" color="secondary" />,
      title: 'Verified Profiles',
      description: 'Identity verification ensures authentic professional profiles',
    },
    {
      icon: <Groups fontSize="large" color="primary" />,
      title: 'Professional Network',
      description: 'Connect with colleagues and follow healthcare professionals',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          /* Trust-forward, clinical aesthetic: subtle radial with navy/teal tint */
          background: 'radial-gradient(1000px 600px at 50% -100px, #DFF2F7 0%, #F7F9FB 60%)',
          color: 'text.primary',
          pt: { xs: 6, md: 12 },
          pb: { xs: 6, md: 12 },
          minHeight: { xs: '60vh', md: '68vh' },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: 1120 }}>
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: '2.1rem', md: '3.2rem' },
              lineHeight: { xs: 1.2, md: 1.1 },
              textAlign: 'center',
              color: 'primary.main',
            }}
          >
            The professional directory for medical providers
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, textAlign: 'center', color: 'text.secondary' }}>
            Search, claim, and share your verified medical CV
          </Typography>

          {/* Search Bar */}
          <Box sx={{ justifyContent: 'center', px: { xs: 2, md: 0 }, width: '100%' }}>
            <SearchBar hero placeholder="Search by name, +handle, specialty, NPI, or location…" />
          </Box>

          {/* Mobile primary CTA under search */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2 }}>
            <Button variant="contained" fullWidth sx={{ height: 48 }} onClick={() => navigate('/search')}>
              Find Your Profile
            </Button>
          </Box>

          {/* Desktop CTAs under search */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: 2, mt: 2 }}>
            <Button variant="contained" onClick={() => navigate('/search')}>Find your profile</Button>
            <Button variant="outlined" onClick={() => navigate('/search')} startIcon={<SearchIcon />}>Search directory</Button>
          </Box>

          {/* Quick search suggestions */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, textAlign: 'center', color: 'text.secondary' }}>
              Popular searches:
            </Typography>
            <Box sx={{
              display: 'flex',
              gap: 1,
              overflowX: { xs: 'auto', md: 'visible' },
              flexWrap: { xs: 'nowrap', md: 'wrap' },
              maxHeight: { md: 80 }, // cap to ~two rows
              WebkitOverflowScrolling: 'touch',
              px: { xs: 0.5, md: 0 },
              justifyContent: { md: 'center' },
            }}>
              {['Internal Medicine (MD)', 'Family Medicine (DO)', 'Pediatrics (NP)', 'Cardiology (MD)', 'Dentistry (DDS)', 'Physician Assistant (PA)'].map((label) => (
                <Chip
                  key={label}
                  label={label}
                  onClick={() => {
                    const spec = label.split(' (')[0];
                    navigate(`/search?specialty=${encodeURIComponent(spec)}`);
                  }}
                  sx={{
                    backgroundColor: 'rgba(15,163,177,0.1)',
                    color: 'primary.main',
                    '&:hover': { backgroundColor: 'rgba(15,163,177,0.18)' },
                    flex: '0 0 auto',
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Social proof strip (desktop only) */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: 3, mt: 4, opacity: 0.95 }}>
            <Typography variant="body1">Profiles sourced from official U.S. NPI records</Typography>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
          Why mdcv.me?
        </Typography>
        <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: { xs: 4, md: 6 } }}>
          The trusted platform for medical professionals to manage their digital presence
        </Typography>

        <Box sx={{ display: 'grid', gap: { xs: 2, md: 3 }, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
          {features.map((feature, index) => (
            <Card key={index} sx={{ height: '100%', textAlign: 'left', py: { xs: 2, md: 3 }, px: { xs: 2, md: 2.5 } }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ mb: { xs: 1.5, md: 2 }, '& svg': { fontSize: { xs: 24, md: 32 } } }}>{feature.icon}</Box>
                <Typography component="h3" sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 600, mb: 1 }}>
                  {feature.title}
                </Typography>
                <Typography sx={{ fontSize: { xs: 14, md: 16 }, color: 'text.secondary' }}>
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>

      {/* How it works / For businesses */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>How it works</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Find your profile, verify your identity, and share your professional CV with a custom handle.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/search')}>Search providers</Button>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>For Businesses</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Offer perks to verified medical professionals and attract loyal customers.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/merchants')}>Partner with us</Button>
            </CardContent>
          </Card>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: 'background.paper', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h2" gutterBottom>
            Ready to claim your profile?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Join thousands of medical professionals managing their digital CV on mdcv.me
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/search')}
            startIcon={<SearchIcon />}
          >
            Find Your Profile
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default HomePage;
